// React
import React from 'react';

// Prime React
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';

// Planner functions
import { formatDate, getEmployeeLink, getTravellerLink, getProductHTML, getOrderLink, getEditButton } from '../planner/planner';

// Additional imports
import { decode } from 'html-entities';

export function ScrapTable(props) {

	// List of all possible columns
	let columns = [
		{
			name: 'date_reported',
			header: 'Reported Date',
			sortable: false,
			output: getDate,
			capability: props.capabilities.read_scraps
		},
		{
			name: 'reported_by',
			header: 'Reported By',
			sortable: false,
			output: getReportedBy,
			capability: props.capabilities.read_scraps
		},
		{
			name: 'status',
			header: 'Status',
			sortable: false,
			output: getStatus,
			capability: props.capabilities.read_scraps
		},
		{
			name: 'material',
			header: 'Material',
			sortable: false,
			output: getMaterial,
			capability: props.capabilities.read_scraps
		},
		{
			name: 'material_qty',
			header: 'Quantity',
			sortable: false,
			output: getMaterialQuantity,
			capability: props.capabilities.read_scraps
		},
		{
			name: 'material_average_cost_price',
			header: 'Material Cost Price',
			sortable: false,
			output: getMaterialAverageCostPrice,
			capability: props.capabilities.read_products
		},
		{
			name: 'material_total_cost_price',
			header: 'Material Total Price',
			sortable: false,
			output: getMaterialTotalCostPrice,
			capability: props.capabilities.read_products
		},
		{
			name: 'fault',
			header: 'Fault',
			sortable: false,
			output: getFault,
			capability: props.capabilities.read_scraps
		},
		{
			name: 'product',
			header: 'Product',
			sortable: false,
			output: getProduct,
			capability: props.capabilities.read_scraps
		},
		{
			name: 'traveller',
			header: 'Traveller #',
			sortable: false,
			output: getTraveller,
			capability: props.capabilities.read_scraps
		},
		{
			name: 'order',
			header: 'Order #',
			sortable: false,
			output: getOrder,
			capability: props.capabilities.read_scraps
		},
		{
			name: 'date_replaced',
			header: 'Date Replaced',
			sortable: false,
			output: getDateReplaced,
			capability: props.capabilities.read_scraps
		},
		{
			name: 'view_button',
			header: '',
			sortable: false,
			output: getButton,
			capability: props.capabilities.edit_scraps
		},
	]

	/**
	 * Format the reported date for the scrap
	 * 
	 * @param {Object} rowData The data associated to the row
	 * @returns {html} The formatted date and time
	 */
	function getDate(rowData) {
		return formatDate(rowData.date_reported, true);
	}

	/**
	 * Get the reported by user
	 * 
	 * @param {Object} rowData The data associated to the row
	 * @returns {html} The reported by user
	 */
	function getReportedBy(rowData) {
		return getEmployeeLink(rowData.reported_by_user_id, rowData.reported_by_display_name, props.capabilities)
	}

	/**
	 * Format the status for the scrap
	 * 
	 * @param {Object} rowData The data associated to the row
	 * @returns {html} The formatted date and time
	 */
	function getStatus(rowData) {
		return <div className={"badge bg-planner-scrap-" + rowData.status_label.toLowerCase()}>{rowData.status_label}</div>;
	}

	/**
	 * Format the material that has been scrapped
	 * 
	 * @param {Object} rowData The data associated to the row
	 * @returns {html} The formatted date and time
	 */
	function getMaterial(rowData) {
		return getProductHTML(rowData.material_id, rowData.material_sku, rowData.material_description, props.capabilities, rowData.material_qty)
	}

	/**
	 * Format the material quantity for the scrap
	 * 
	 * @param {Object} rowData The data associated to the row
	 * @returns {html} The formatted date and time
	 */
	function getMaterialQuantity(rowData) {
		return <>{rowData.material_qty}</>
	}

	/**
	 * Format the reported date for the scrap
	 * 
	 * @param {Object} rowData The data associated to the row
	 * @returns {html} The formatted date and time
	 */
	function getMaterialAverageCostPrice(rowData) {
		if(rowData.material_average_cost_price) {
			return decode(rowData.material_average_cost_price);
		} else {
			return null;
		}
	}

	/**
	 * Get the total cost price for the scrap
	 * 
	 * @param {Object} rowData The data associated to the row
	 * @returns {html} The formatted date and time
	 */
	function getMaterialTotalCostPrice(rowData) {
		return decode(rowData.material_total_cost_price);
	}

	/**
	 * Get a badge for the fault of the scrap
	 * 
	 * @param {Object} rowData The data associated to the row
	 * @returns {html} The fault inside of a badge of the scrap
	 */
	function getFault(rowData) {
		let classColour = 'primary';

		if(rowData.scrap_cat_name === 'Uncategorised') {
			classColour = 'danger';
		}

		return <div className={"badge bg-" + classColour}>{rowData.scrap_cat_name}</div>
	}

	/**
	 * Get HTML for the product information
	 * 
	 * @param {Object} rowData The data associated to the row
	 * @returns {html} Product information with a tooltip description
	 */
	function getProduct(rowData) {
		return getProductHTML(rowData.product_id, rowData.product_sku, rowData.product_description, props.capabilities, rowData.product_qty)
	}

	/**
	 * Get the traveller number and link if user has capability to view travellers
	 * 
	 * @param {Object} rowData The data associated to the row
	 * @returns {html} The traveller ID with a clickable link to go to that traveller page
	 */
	function getTraveller(rowData) {
		return getTravellerLink(rowData.traveller_id, rowData.traveller_number, props.capabilities)
	}

	/**
	 * Get the order number and link if user has capability to view orders
	 * 
	 * @param {Object} rowData The data associated to the row
	 * @returns if users can read jobs, output html link to the job
	 */
	function getOrder(rowData) {
		return getOrderLink(rowData.order_id, rowData.order_number, props.capabilities)
	}

	/**
	 * Get the date replaced
	 * 
	 * @param {Object} rowData The data associated to the row
	 * @returns HTML output for the date replaced
	 */
	function getDateReplaced(rowData) {
		return formatDate(rowData.date_replaced);
	}

	/**
	 * Get the edit button for the scrap
	 * 
	 * @param {Object} rowData The data associated to the row
	 * @returns {html} The edit button for the scrap
	 */
	function getButton(rowData) {
		return getEditButton("/scrap/" + rowData.id);
	}

	return (
		<DataTable value={props.scraps}>
			<Column key="scrap_number" field="scrap_number" header="Scrap #" sortable={false} />
			{
				columns.map(column => {
					return props.columns.includes(column.name) && column.capability == true && (
						<Column key={column.name} field={column.name} header={column.header} sortable={column.sortable} body={column.output} />
					)
				})
			}
		</DataTable>
	)
}